import * as React from 'react';
import { FormikErrors } from 'formik';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import errorSvg from '../../../images/error.svg';
import { hasValue } from '../../../lib/utils';

interface ValidationSummaryProps {
	errors: FormikErrors<unknown>;
	touched: Record<string, boolean>;
}

const ValidationSummary: React.FC<ValidationSummaryProps> = ({ errors, touched }) => {
	const location = useLocation();

	const isIdPage = React.useMemo(() => {
		const pathname = location.pathname.toLowerCase();
		const search = location.search.toLowerCase();
		return (
			pathname.includes('identification') ||
			pathname.includes('/id') ||
			search.includes('identification') ||
			search.includes('pageslug=identification') ||
			search.includes('pageslug=id')
		);
	}, [location]);

	const getSectionFromFieldPath = React.useCallback((fieldPath: string): string | null => {
		const firstPart = fieldPath.split('.')[0];

		const formatSectionName = (name: string): string => {
			const specialCases: Record<string, string> = {
				addressHistories: 'Address History',
				additionalNames: 'Additional Names',
				primaryName: 'Primary Name',
				identityDocuments: 'Identity Documents',
				hiddenError: 'Name Change Policies'
			};

			if (specialCases[name]) {
				return specialCases[name];
			}

			return name
				.replace(/([A-Z])/g, ' $1')
				.replace(/^./, (str) => str.toUpperCase())
				.replace(/\bDetails\b/g, 'Details')
				.trim();
		};

		return formatSectionName(firstPart);
	}, []);

	const errorMessages = React.useMemo(() => {
		if (isIdPage) {
			const sectionErrors = new Map<string, string[]>();

			const collectErrors = (obj: any, touchedObj: any = {}, path = '') => {
				if (!obj || typeof obj !== 'object') return;

				Object.keys(obj).forEach((key) => {
					const currentPath = path ? `${path}.${key}` : key;
					const value = obj[key];
					const isTouched = touchedObj && touchedObj[key];
					if (typeof value === 'string' && isTouched) {
						const sectionName = getSectionFromFieldPath(currentPath);

						if (sectionName) {
							if (!sectionErrors.has(sectionName)) {
								sectionErrors.set(sectionName, []);
							}
							sectionErrors.get(sectionName)!.push(value);
						} else {
							sectionErrors.set(value, [value]);
						}
					} else if (typeof value === 'object' && value !== null) {
						collectErrors(value, touchedObj ? touchedObj[key] : {}, currentPath);
					}
				});
			};

			collectErrors(errors, touched);

			const finalMessages: string[] = [];
			sectionErrors.forEach((errorList, sectionName) => {
				finalMessages.push(sectionName);
			});

			return [...new Set(finalMessages)].filter((msg) => hasValue(msg) && msg.trim() !== '');
		}
		const messages: string[] = [];

		const collectErrors = (obj: any, touchedObj: any = {}, path = '') => {
			if (!obj || typeof obj !== 'object') return;

			Object.keys(obj).forEach((key) => {
				const currentPath = path ? `${path}.${key}` : key;
				const value = obj[key];
				const isTouched = touchedObj && touchedObj[key];

				if (typeof value === 'string' && isTouched) {
					messages.push(value);
				} else if (typeof value === 'object' && value !== null) {
					collectErrors(value, touchedObj ? touchedObj[key] : {}, currentPath);
				}
			});
		};

		collectErrors(errors, touched);

		return [...new Set(messages)].filter((msg) => hasValue(msg) && msg.trim() !== '');
	}, [errors, touched, isIdPage, getSectionFromFieldPath]);

	if (errorMessages.length === 0) {
		return null;
	}

	return (
		<ValidationSummaryContainer>
			<ValidationSummaryHeader>
				<img src={errorSvg} alt="Error Icon" />
				<h3>Please fix errors in the following sections:</h3>
			</ValidationSummaryHeader>
			<ValidationSummaryList>
				{errorMessages.map((message) => (
					<ValidationSummaryItem key={message}>{message}</ValidationSummaryItem>
				))}
			</ValidationSummaryList>
		</ValidationSummaryContainer>
	);
};

export default ValidationSummary;

const ValidationSummaryContainer = styled.div`
	position: relative;
	padding: 8px;
	margin-bottom: 24px;
	background-color: #ffd5d8;
	border: 1px solid #c70011;
	border-radius: 4px;
	text-align: left;
	color: #ff5151;
`;

const ValidationSummaryHeader = styled.div`
	display: flex;
	align-items: center;
	margin: 10px 0px;

	img {
		width: 20px;
		height: 20px;
		margin-right: 8px;
	}

	h3 {
		margin: 0;
		padding-left: 8px;
		display: inline-block;
		width: calc(100% - 20px);
		font-size: 15px;
		line-height: 15px;
		font-weight: Bold;
		color: #ff5151;
	}
`;

const ValidationSummaryList = styled.ul`
	margin: 0;
	padding-left: 28px;
	list-style-type: disc;
`;

const ValidationSummaryItem = styled.li`
	margin-bottom: 5px;
	font-size: 15px;
    line-height: 15px

	&:last-child {
		margin-bottom: 0;
	}
`;
